import { Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { AlertTriangle, CheckCircle } from 'react-feather';

/**
 * MED-876 AC2 — single row in the per-step AI Suggestions section.
 *
 * Two visual variants:
 *   - **Primary** (PURPOSE / OPENING PROMPT / IF GROUP STALLS) — purple border
 *     and an "In Use" button (purple-filled when active, outlined when not).
 *   - **Alternative** (ALTERNATIVE PROMPT) — gray border and a "Use This"
 *     outlined button (no "In Use" state of its own — clicking promotes this
 *     alternative into the parent's `speaking` slot).
 *
 * Visual reference: screenshot #2 (Step 2 expanded).
 */
export interface AIPromptRowProps {
  label: string;
  text: string;
  subText?: string;
  variant: 'primary' | 'alternative';
  inUse: boolean;
  /** Whether the In Use button is disabled (e.g. while the parent is saving). */
  disabled?: boolean;
  /**
   * MED-1034 — when set, this In-Use row is a persisted coach selection the
   * current session materials no longer yield (a fresh suggestion came back
   * empty for this slot). We keep the row visible and toggleable so the coach's
   * choice isn't silently dropped, but mute the text and show this warning so it
   * no longer contradicts the "no usable prompt" AI note below.
   */
  staleNote?: string;
  /** Click handler for primary rows (toggles In Use) and alternative rows ("Use This"). */
  onAction: () => void;
}

export function AIPromptRow({
  label,
  text,
  subText,
  variant,
  inUse,
  disabled,
  staleNote,
  onAction,
}: AIPromptRowProps) {
  const isPrimary = variant === 'primary';
  const borderColor = isPrimary ? '#c8b8ff' : '#d9d9d9';
  const labelColor = isPrimary ? '#7c4dff' : '#666';

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      style={{ borderColor, borderWidth: 1.5, backgroundColor: '#fff' }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text
            size="xs"
            fw={700}
            style={{
              color: labelColor,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {label}
          </Text>
          <Text
            size="sm"
            style={{
              lineHeight: 1.5,
              color: staleNote ? '#868e96' : undefined,
            }}
          >
            {text}
          </Text>
          {staleNote && (
            <Group
              gap={4}
              align="center"
              wrap="nowrap"
              style={{ marginTop: 2 }}
            >
              {/* Decorative — the meaning is fully carried by the adjacent text. */}
              <AlertTriangle
                size={12}
                color="#b54708"
                aria-hidden
                style={{ flexShrink: 0 }}
              />
              <Text size="xs" style={{ color: '#b54708', lineHeight: 1.4 }}>
                {staleNote}
              </Text>
            </Group>
          )}
          {subText && (
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
              {subText}
            </Text>
          )}
        </Stack>
        <Box style={{ flexShrink: 0 }}>
          {isPrimary ? (
            <Button
              size="xs"
              radius="xl"
              disabled={disabled}
              variant={inUse ? 'filled' : 'light'}
              color={inUse ? 'violet' : 'gray'}
              leftSection={inUse ? <CheckCircle size={12} /> : undefined}
              onClick={onAction}
            >
              {inUse ? 'In Use' : 'Use This'}
            </Button>
          ) : (
            <Button
              size="xs"
              radius="xl"
              variant="outline"
              color="gray"
              disabled={disabled}
              onClick={onAction}
            >
              Use This
            </Button>
          )}
        </Box>
      </Group>
    </Paper>
  );
}

export default AIPromptRow;
